<script setup>
/* eslint semi: 1 */
import { onMounted, defineOptions, getCurrentInstance, inject, ref, onUnmounted } from 'vue';
import { FwbButtonGroup, FwbTooltip, FwbButton } from 'flowbite-vue';
import { useI18n } from 'vue-i18n';

// components
import ScheduleGridList from '@/components/common/schedule/schedule-grid-list.vue';
import ScheduleTableList from '@/components/common/schedule/schedule-table-list.vue';
import ScheduleSearch from '@/components/common/schedule/schedule-search.vue';

// mixins
import AuthMixin from '@/components/mixins/auth-mixin';
import EventBus from '@/components/mixins/event-bus-mixin';

// composables
import { useMixpanel } from '@base-shared/composables/use-mixpanel';
import { useScheduleListStore } from '@/pinia/stores/schedule/scheduleListStore';
import { useScheduleList } from '@/composables/schedule/use-schedule-list';
import { useSchedulePreviewStatus } from '@/composables/schedule/use-schedule-preview-status';
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';

defineOptions({
  mixins: [AuthMixin]
});

const { proxy } = getCurrentInstance();
const $utils = inject('$utils');
const $router = inject('$routerInstance');
const store = useScheduleListStore();
const { t } = useI18n();
const cognitoAuthStore = useCognitoAuthStore();
const {
  currentPage,
  pagedScheduleItems,
  totalItemsCount,
  totalPages,
  hasPreviousPage,
  hasNextPage,
  minItemsCount,
  maxItemsCount,
  isFetchingNextBatch,
  showGridLoader,
  notificationPayload,
  listViewMode,
  handlePaginatorPageSelect,
  loadInitialSchedulePage,
  loadSchedulePage,
  deleteSchedule,
  closeNotification,
  setSchedulePlayerStatus,
  setListViewMode
} = useScheduleList();

const { handlePreviewLoad, postMessageToAllVisibleSchedules } = useSchedulePreviewStatus();

const cleanupVisibilityListener = ref(null);

function handleScheduleCardClick (_schedule) {
  // TODO(schedule-v2): Route to selected schedule item page.
}

function handleScheduleDeleteClick (schedule) {
  if (!schedule?.scheduleId) return;

  EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
    type: 'warning',
    description: t('confirm.removeTheMediaFromTheSchedule'),
    callbacks: {
      eventOKButtonClick: async () => {
        $utils.mainLoader(true);
        await deleteSchedule(schedule.scheduleId);
        $utils.mainLoader(false);
      }
    }
  });
}

/**
 * Listens for postMessage updates from the schedule preview iframes and
 * records the resulting online/offline status in the store. Completely
 * isolated from the legacy schedule page's own `window.message` listener.
 */
function handleSVGMessage (event) {
  const sessionStage = cognitoAuthStore.sessionStage ?? CONFIG.GLOBAL_STAGE;
  const apiOrigin = new URL($utils.getStageAPIUrl(sessionStage)).origin;
  if (event.origin !== apiOrigin || typeof event.data !== 'object' || event.data === null) return;
  if (!event.data.serialNumber) return;

  setSchedulePlayerStatus(event.data.serialNumber, event.data.status);
}

async function refetchSchedulePages () {
  await loadSchedulePage();
}

function goToPlayerSetupPage (schedule) {
  $router.push(`/setup/${schedule.scheduleId}/${schedule.name}`, () => {});
}

onMounted(() => {
  window.addEventListener('message', handleSVGMessage);

  proxy.isAuthenticated({
    callback: async () => {
      useMixpanel().track('Schedule');
      const route = $router.currentRoute.value;
      await loadInitialSchedulePage(route.query);
    }
  });

  cleanupVisibilityListener.value = $utils.handleDocumentVisibilityChange(
    () => postMessageToAllVisibleSchedules(pagedScheduleItems.value, false),
    () => postMessageToAllVisibleSchedules(pagedScheduleItems.value, true)
  );
});

onUnmounted(() => {
  window.removeEventListener('message', handleSVGMessage);
  cleanupVisibilityListener.value?.();
});

</script>
<template>
  <div id="schedulePage" class="page white py-6">
    <main
      class="flex flex-col h-full gap-4 max-w-7xl flex-1 min-w-0 mx-auto"
    >
      <!-- Header -->
      <div class="w-full relative bg-white p-4">
        <div class="flex items-center justify-between">
          <ScheduleSearch @apply="refetchSchedulePages" />
          <FwbButtonGroup>
            <FwbTooltip placement="bottom" theme="light">
              <template #trigger>
                <FwbButton
                  class="justify-center focus:ring-transparent!"
                  data-component-element-id="schedule-grid-view-btn"
                  color="light"
                  square
                  size="lg"
                  @click="setListViewMode('grid')"
                >
                  <BaseIconSvg
                    class="w-4! h-4!"
                    :class="{ 'text-arya-500': listViewMode === 'grid' }"
                    name="flowbite:grid-outline"
                  />
                </FwbButton>
              </template>
              <template #content>
                {{ $t('label.grid') }}
              </template>
            </FwbTooltip>

            <FwbTooltip placement="bottom" theme="light">
              <template #trigger>
                <FwbButton
                  class="justify-center focus:ring-transparent!"
                  data-component-element-id="schedule-table-view-btn"
                  color="light"
                  square
                  size="lg"
                  @click="setListViewMode('list')"
                >
                  <BaseIconSvg
                    class="w-4! h-4!"
                    :class="{ 'text-arya-500': listViewMode === 'list' }"
                    name="flowbite:list-outline"
                  />
                </FwbButton>
              </template>
              <template #content>
                {{ $t('template.list') }}
              </template>
            </FwbTooltip>
          </FwbButtonGroup>
        </div>
      </div>

      <div class="flex flex-col flex-1 min-h-0 w-full mb-8">
        <div
          v-if="notificationPayload"
          class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40"
        >
          <BaseNotificationAlertsItem
            v-if="notificationPayload"
            :notifId="notificationPayload.notifId"
            :type="notificationPayload.type"
            :icon="notificationPayload.icon"
            :title="notificationPayload.title"
            :html-message="notificationPayload.message"
            :requestId="notificationPayload.requestId"
            :closable="notificationPayload.closable"
            @dismiss="closeNotification"
          />
        </div>
        <div
          v-if="listViewMode === 'grid'"
          class="flex-1 w-full h-full overflow-auto"
        >
          <ScheduleGridList
            :items="pagedScheduleItems"
            :showLoader="showGridLoader"
            :enableDelete="true"
            :player-status-by-id="store.playerStatusById"
            @card-click="handleScheduleCardClick"
            @delete-click="handleScheduleDeleteClick"
            @preview-load="handlePreviewLoad"
            @setup-click="goToPlayerSetupPage"
          />
        </div>

        <div
          v-else
          class="flex-1 w-full h-full overflow-x-auto overflow-y-hidden"
        >
          <ScheduleTableList
            :items="pagedScheduleItems"
            :show-loader="showGridLoader"
            :enable-delete="true"
            :player-status-by-id="store.playerStatusById"
            @card-click="handleScheduleCardClick"
            @delete-click="handleScheduleDeleteClick"
            @preview-load="handlePreviewLoad"
            @iframe-mouseenter="() => {}"
            @iframe-mouseleave="() => {}"
            @setup-click="goToPlayerSetupPage"
          />
        </div>

        <div
          v-if="!showGridLoader && pagedScheduleItems.length > 0"
          class="w-full flex items-center justify-between gap-3 py-3"
        >
          <div class="ml-auto">
            <Paginator
              pagination-type="manual"
              :current-page="currentPage"
              :total-pages="totalPages"
              :min-items-count="minItemsCount"
              :max-items-count="maxItemsCount"
              :total-items-count="totalItemsCount"
              :is-previous-disabled="!hasPreviousPage"
              :is-next-disabled="!hasNextPage || isFetchingNextBatch"
              @page-select="handlePaginatorPageSelect"
            />
          </div>
        </div>
      </div>
    </main>
  </div>
</template>
<style></style>
